import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import useShow from '../../hooks/useShow';
import {getPromotionMessage, stopShowMessage, acceptPromotion, promotionAnalytics} from '../../redux/event/eventActions';
import PromotionMessage from '../promotionMessage/PromotionMessage';
import PromotionPopup from '../prompotionPopup/PromotionPopup';
import PromotionPopupContent from './PromotionPopupContent';
import PromotionWhatsappButton from './PromotionWhatsappButton';
import {getStaticImage} from '../../globalVariables/GlobalVariables';
import {logGoogleEvent} from '../../utils/analytics';

// export const promotionColors = {
//     eventReady: ['#5FBF9C', '#4695A9', '#16DC99'],
//     salesDecline: ['#5D1D83', '#332673', '#D963F9'],
//     oneWeekBeforeEvent: ['#6656C9', '#8594E7', '#FBD24A'],
//     lastMinute: ['#878ADB', '#E176A9', '#9F63FF']
// };
export const colors = ['#FCE79B', '#FFDA56', '#101010'];
export const PUBLISH_EVENT_PROMOTION = 5;

// views already logged this session (keyed by eid-pmid, since the same promotion can
// legitimately show on different events) - the redux message survives remounts, so the
// [pmid] effect would otherwise re-log a view on every return to the page
const loggedPromotionViews = new Set<string>();

function ManagePromotion() {
    const showPromotion = useShow();
    const showPromotionPopup = useShow();
    const {pmid, type, termsApproved, commission} = useSelector((state: any) => state.event.promotionMessage) || {};
    const eid = useSelector((state: any) => state.event.eid);
    // const colors = promotionColors[type] || [];

    const backgroundColor = type === 'eventReady' ? '#FFFFFF' : `linear-gradient(180deg, ${colors[0] || ''} 0%, ${colors[1] || ''} 100%);`;
    const backgroundImage = getStaticImage(`app/promotion/${type}Promotion.png`);

    const {t} = useTranslation('promotionMessage');

    // snapshot of the shown message - state.event.promotionMessage is shared with the
    // publish flow and can be overwritten while this message is still on screen
    const shownMessage = useRef<{pmid?: number, type?: string}>({});
    const messageClickLogged = useRef(false);
    const popupClickLogged = useRef(false);

    const logAnalytics = (eventName: string, params?: Record<string, unknown>) => {
        const shown = shownMessage.current;
        logGoogleEvent(`realtime_${eventName}`, shown.pmid, `pmid-${shown.pmid}`, {
            popup_name: 'promotionMessage',
            promotion_type: shown.type,
            feature: 'realtime',
            ...params
        });
    };

    // buttons stay clickable during the 300ms close animation - log one click per open
    const logClickOnce = (logged: React.MutableRefObject<boolean>, eventName: string, params?: Record<string, unknown>) => {
        if(logged.current)
            return;
        logged.current = true;
        logAnalytics(eventName, params);
    };

    const closeMessage = () => {
        showPromotion.close();
        stopShowMessage(pmid);
        promotionAnalytics(pmid, 'cancel_click').then();
        logClickOnce(messageClickLogged, 'promotion_message_close_click', {button_name: 'close'});
    };

    const accept = () => {
        acceptPromotion(pmid);
        showPromotionPopup.open();
        showPromotion.close();
        promotionAnalytics(pmid, 'send_click').then();
        logClickOnce(messageClickLogged, 'promotion_message_accept_click', {button_name: 'accept'});
    };

    const closePopup = (e?: Event | React.SyntheticEvent) => {
        logClickOnce(popupClickLogged, 'promotion_popup_close_click', {button_name: 'close', popup_name: 'promotionPopup'});
        return showPromotionPopup.close(e);
    };

    const thanksClick = () => {
        logClickOnce(popupClickLogged, 'promotion_popup_thanks_click', {button_name: 'thanks', popup_name: 'promotionPopup'});
        showPromotionPopup.close();
    };
    useEffect(() => {
        getPromotionMessage('realtime').then();
    }, []);

    useEffect(() => {
        if(pmid && pmid !== PUBLISH_EVENT_PROMOTION) {
            shownMessage.current = {pmid, type};
            messageClickLogged.current = false;
            showPromotion.open();
            promotionAnalytics(pmid, 'view').then();
            const viewKey = `${eid}-${pmid}`;
            if(!loggedPromotionViews.has(viewKey)) {
                loggedPromotionViews.add(viewKey);
                logAnalytics('promotion_message_view');
            }
        }
    }, [pmid]);

    useEffect(() => {
        if(showPromotionPopup.isOpen) {
            popupClickLogged.current = false;
            logAnalytics('promotion_popup_open', {popup_name: 'promotionPopup'});
        }
    }, [showPromotionPopup.isOpen]);
    if(pmid === PUBLISH_EVENT_PROMOTION) {
        return <></>;
    }
    if(!type) return <></>;
    return (
        <>
            <PromotionMessage
                show={showPromotion}
                firstTitle={t(`${type}TitleFirst`)}
                secondTitle={t(`${type}TitleSecond`)}
                text={t(`${type}Text`, {commission})}
                background={{image: backgroundImage, color: backgroundColor}}
                button={{onClick: accept, text: t(`${type}Accept`), color: colors[2]}}
                close={closeMessage}
            />
            {!!showPromotionPopup.isOpen && (
                <PromotionPopup
                    show={{...showPromotionPopup, close: closePopup}}
                    color={backgroundColor}
                    image={backgroundImage}
                    button={{onClick: thanksClick, text: t('thanks'), disabled: termsApproved != 1}}
                    extraButton={<PromotionWhatsappButton pmid={pmid} feature="realtime" />}
                >
                    <PromotionPopupContent pmid={pmid} feature="realtime" />
                </PromotionPopup>
            )}
        </>
    );
}

export default ManagePromotion;
